import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { useRuf } from "../schale/Ruf";
import { useTelefon } from "../schale/geraet";
import { useAufgabeNehmen, useAufnahmeNehmen, useMangelErledigen } from "../daten/haken";
import type { Aufgabe } from "../daten/vorbereitung";
import type { Pruefzeile, StartKarteZeile, Wiedervorlage } from "../daten/typen";
import { tagKurz } from "../daten/einsatzzeit";
import { Marke } from ".";
import { Wer, aufgabenFehler, wannText } from "./Vorbereitung";
import { tageBis } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Eine Zeile in „Was ist zu tun?" (Startseite, AD4).

   **Die Spalten laufen von links nach rechts vom Was zum Griff**
   (16.09.2026). Luca: *„Was ist zu tun heißt, was es zu tun gibt. Ganz
   links soll stehen, was zu tun ist, das Zweite von links geprüft, das
   Dritte von links … und der Haken soll ganz rechts sein."*

     ganz links   die Tätigkeit — Vorbereitung, Übergabe, Rücknahme,
                  und darunter, wann es dran ist
     in der Mitte das Gerät und darunter Kunde, Ort, Bediener
     ganz rechts  wer es hat und der eine Knopf, der gerade geht

   Bis dahin standen Wer und „Nehmen" als dritte Zeile unter dem Text, um
   68 px eingerückt, und die erste Spalte war so breit wie ihre Marke — bei
   „Vorbereitung" anders als bei „Frist". Drei Ebenen, die in jeder Zeile
   woanders anfingen.

   **Zwei Spalten statt vier**, weil die Karte 300 px breit ist: Vier
   Spalten nebeneinander hieße, das Gerät auf drei Buchstaben zu kürzen.
   Das Maß der linken Spalte steht als `--karte-marke` in `stile/start.css`
   und gilt auch für die Karte „Heute" daneben — damit die beiden wie eine
   Familie aussehen und nicht wie zwei Entwürfe.

   **Wohin es geht**, steht hier und nicht im Server: Wege sind Sache des
   Routers (routen.ts, `HAKT`). Die Vorbereitung und der Mangel führen auf
   das Blatt der Vorbereitung, Übergabe und Rücknahme in den Auftrag — dort
   steht der Knopf zum Protokoll —, die Ortsaufnahme in ihren Pool.

   Die Zeile ist kein `<button>` im Ganzen: Ein Knopf im Knopf ist kein
   gültiges HTML, und „Nehmen" soll nicht nebenbei die Maske wechseln.
   ══════════════════════════════════════════════════════════════════ */

export function Aufgabenzeile({ a, lenkt }: {
  a: Aufgabe;
  /** Disposition oder Verwaltung — sie erledigen den Mangel. */
  lenkt: boolean;
}) {
  const t = useT();
  const ruf = useRuf();
  const gehZu = useNavigate();
  const nehmen = useAufgabeNehmen();
  const aufnahme = useAufnahmeNehmen();
  const mangel = useMangelErledigen();
  const laeuft = nehmen.isPending || aufnahme.isPending || mangel.isPending;

  const hin = () => {
    if ((a.art === "vorbereitung" || a.art === "mangel") && a.belegungId) {
      void gehZu({ to: "/einsatz/$belegung/vorbereiten", params: { belegung: a.belegungId } });
    } else if (a.art === "ortsaufnahme") {
      void gehZu({ to: "/aufnahmen" });
    } else if (a.vorgang) {
      void gehZu({ to: "/auftraege/$nr", params: { nr: a.vorgang } });
    } else if (a.belegungId) {
      /* In den Einsatz (bis zum 19.09.2026 `/handy/<belegung>?art=…`). Ob
         Übergabe oder Rücknahme ansteht, weiß der Einsatz aus der Lage und
         nicht aus der Adresse. */
      void gehZu({ to: "/einsatz/$belegung", params: { belegung: a.belegungId } });
    }
  };

  /* Ein Fehler bleibt an der Zeile stehen, nicht in einer Meldung, die
     nach drei Sekunden weg ist: „T. Brand hat das schon genommen" ist eine
     Auskunft über genau diese Zeile. */
  const [fehlerStand, setzeFehler] = useState<ReturnType<typeof aufgabenFehler> | null>(null);
  const fehler = (e: unknown) => setzeFehler(aufgabenFehler(e));

  const umstellen = (wer: null | undefined, bestaetigt = false) => {
    setzeFehler(null);
    const gut = { onSuccess: () => ruf(wer === null ? "vorbereitung.zurueck" : "vorbereitung.genommen") };
    if (a.art === "ortsaufnahme" && a.aufnahmeId) {
      aufnahme.mutate({ id: a.aufnahmeId, ...(wer === null ? { wer: null } : {}) }, { ...gut, onError: fehler });
    } else if (a.art !== "ortsaufnahme" && a.belegungId) {
      nehmen.mutate(
        { art: a.art, belegungId: a.belegungId, ...(wer === null ? { wer: null } : {}), ...(bestaetigt ? { bestaetigt } : {}) },
        { ...gut, onError: fehler },
      );
    }
  };

  const dazu = a.art === "mangel"
    ? t("aufgaben.mangelWas", { fragen: a.maengel.join(` ${t("allgemein.trennpunkt")} `) })
    : [a.kunde, a.ort || a.ortschaft, a.bediener.length
      ? t("aufgaben.bediener", { name: a.bediener.map((b) => b.name).join(", ") })
      : ""].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `);

  return (
    <div className={`aufgabe${a.ueberfaellig ? " spaet" : ""}${a.art === "mangel" ? " mangel" : ""}`}>
      <button
        type="button"
        className="aufgabe-weg"
        onClick={hin}
        aria-label={t("aufgaben.zeileOeffnen", { art: t(`aufgaben.art.${a.art}`), kennung: a.kennung || a.kunde })}
      >
        <Marke art={a.art === "mangel" ? "stopp" : a.ueberfaellig ? "acht" : ""}>
          {t(`aufgaben.art.${a.art}`)}
        </Marke>
        <span className="aufgabe-was">
          {a.kennung ? <b>{a.kennung}</b> : null}
          <span>{a.art === "ortsaufnahme" ? a.kunde : a.typ || a.kunde}</span>
        </span>
        {/* Die Ortsaufnahme hat keinen Tag — außer, es ist ein Termin ausgemacht (0180). */}
        <span className="aufgabe-zeit">{a.art === "ortsaufnahme" && !a.termin ? "" : wannText(t, a.faelligIso, a.tage)}</span>
        {dazu ? <span className="aufgabe-dazu">{dazu}</span> : null}
      </button>

      <span className="aufgabe-griffe">
        <Wer
          genommen={a.genommen}
          meins={a.meins}
          /* Nur, wo die Aufgabe dem Bediener gehört — den Mangel hat die
             Disposition, auch an einer Maschine mit Fahrer. */
          {...(a.pool === "bediener" ? { bediener: a.bediener } : {})}
          darfNehmen={a.darfNehmen}
          darfZurueckgeben={a.darfZurueckgeben}
          nehmen={() => umstellen(undefined)}
          zurueckgeben={() => umstellen(null)}
          laeuft={laeuft}
        />
        {/* Der Mangel hat einen zweiten Griff: Die Disposition hat
            hingesehen und entschieden. Wer ihn genommen hat, steht davor. */}
        {a.art === "mangel" && lenkt ? (
          <button
            type="button"
            className="aufgabe-knopf"
            disabled={laeuft}
            onClick={() => a.belegungId && mangel.mutate(a.belegungId, {
              onSuccess: () => ruf("vorbereitung.mangelErledigt"),
              onError: fehler,
            })}
          >
            {t("vorbereitung.mangelErledigen")}
          </button>
        ) : null}
      </span>
      {fehlerStand ? (
        <span className="aufgabe-fehler" role="alert">
          {t(fehlerStand.pfad, fehlerStand.werte)}
          {fehlerStand.bestaetigbar ? (
            <button type="button" className="aufgabe-knopf" disabled={laeuft} onClick={() => umstellen(undefined, true)}>
              {t("aufgaben.trotzdemNehmen")}
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Eine Zeile unter „Zu prüfen" — Schreibtisch und Telefon.

   ── Was am 18.09.2026 daran falsch war ────────────────────────────

   Luca, beim Durchklicken der Handy-Ansicht: *„Zu ‚Zu prüfen' hast du es
   komplett verkackt. Die Einträge, die bei ‚Zu prüfen' sind, müssen
   überarbeitet werden."*

   Die Zeile war `.karte-zeile` und trug drei Dinge: eine Marke
   (ZURÜCK · KEIN FAHRER · NICHT BESTÄTIGT · UNTERSCHRIFT), die
   Vorgangsnummer und den Kundennamen — auf 390 px abgeschnitten. **Der
   Satz, der sagt, was fehlt, stand im `title`** — und ein `title` ist am
   Finger nichts: Es gibt kein Überfahren. Fünf Zeilen sahen damit gleich
   aus und sagten weder, was zu tun ist, noch wie dringend es ist.

   ── Die Form ist jetzt die von „Was ist zu tun?" ──────────────────

   Dieselben Spalten wie in `Aufgabenzeile` darüber (`.aufgabe` aus
   `stile/vorbereitung.css`), und damit auch dieselbe Anordnung am Telefon
   (`.st-raum .hf-zeilenblatt .aufgabe` in `stile/start.css`):

     links oben    die Art als Marke — worauf gewartet wird
     links unten   **wie alt es ist** — „seit 3 Tagen"
     rechts oben   die Vorgangsnummer und der Kunde
     rechts unten  **was fehlt**, als Satz und nicht als `title`

   Die Zeile führt weiter in den Vorgang; dort steht, was zu tun ist. Sie
   steht hier und nicht in `Start.tsx`, weil der Telefonschirm dieselbe
   Zeile zeichnet — wie `Aufgabenzeile` darüber.

   **Der Kundenname wird nicht mehr abgeschnitten**: `.aufgabe.pruefen`
   lässt ihn umbrechen (`stile/start.css`). In einer Spalte ist Kürzen
   richtig, in einer Karte nicht — 28-designkonzept.md, „Gestapelt wird
   nichts abgeschnitten".

   **Nachtrag 24.09.2026 (Prüfung F-02):** Das Alter kommt jetzt mit
   (`Pruefzeile.seit`) — die Rücknahme, das Protokoll, das Ende der
   Werkstattzeit. Nur `fahrer_fehlt` hat weiter keins; dort ist der Beginn
   des Einsatzes die Dringlichkeit, nicht das Alter.

   ── Was der Zeile an Daten fehlte (18.09.2026) ────────────────────

   Zwei der drei Auskünfte kann `GET /start` heute nicht liefern, und
   geraten wird nichts:

   | | was fehlt | wo es läge |
   |---|---|---|
   | **Gerät** | bei `zurueck`, `tag_nicht_bestaetigt`, `unterschrift_fehlt` | `Pruefzeile.kennung` — bei `fahrer_fehlt` steht das Gerät als `position` da, bei den anderen dreien nichts |
   | **Alter** | bei `zurueck`, `fahrer_fehlt`, `unterschrift_fehlt` | `Pruefzeile.seit` — nur `tag_nicht_bestaetigt` trägt mit `tag` ein Datum |

   Solange sie fehlen, bleibt die linke untere Ecke bei drei der vier
   Arten leer. **Leer ist ehrlicher als gefüllt**: Eine geratene Standzeit
   schickt jemanden in die falsche Reihenfolge.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Wie viele Tage ein Tag (`JJJJ-MM-TT`) zurückliegt. 0 ist heute, negativ
 * heißt: er kommt noch. `null`, wenn das kein Tag ist.
 *
 * Gerechnet wird in **ganzen Tagen der Ortszeit** und nicht in Stunden:
 * „seit 2 Tagen" soll um 23:50 dasselbe sagen wie zehn Minuten später.
 */
function tageSeit(tag: string): number | null {
  // In deutschen Kalendertagen, auch aus einem Zeitpunkt (N8-02).
  const bis = tageBis(tag);
  return bis === null ? null : -bis;
}

/**
 * **Wohin eine Zeile unter „Zu prüfen" führt** — am Schreibtisch und am
 * Telefon derselbe Weg, darum einmal hier.
 *
 * - die Werkstattzeile in die Ressourcen: Ihre `nr` ist die Kennung eines
 *   Geräts, kein Vorgang (22.09.2026);
 * - die fehlende Unterschrift **gleich ins Protokoll** (24.09.2026, Prüfung
 *   F-02): Die Akte schlägt es auf, und oben steht, was zu tun ist;
 * - alles andere in den Vorgang.
 */
export function pruefzeileOeffnen(
  gehZu: ReturnType<typeof useNavigate>,
  z: Pruefzeile,
): Promise<void> {
  if (z.art === "werkstatt_vorbei") return gehZu({ to: "/ressourcen" });
  // Fehlende Stammdaten (W2): `nr` ist die Kundennummer, die Akte öffnet das erste fehlende Feld.
  if (z.art === "stammdaten_fehlen") {
    return gehZu({ to: "/kunden/$nr", params: { nr: z.nr }, search: { luecken: true } });
  }
  if ((z.art === "unterschrift_fehlt" || z.art === "nachtanken") && z.protokoll) {
    return gehZu({ to: "/auftraege/$nr", params: { nr: z.nr }, search: { protokoll: z.protokoll } });
  }
  // Fehlende Pflichtfotos (0173): gleich der Blick auf Gründe und Fotos.
  if (z.art === "fotos_fehlen" && z.belegung) {
    return gehZu({ to: "/auftraege/$nr", params: { nr: z.nr }, search: { fotos: z.belegung } });
  }
  return gehZu({ to: "/auftraege/$nr", params: { nr: z.nr } });
}

/** Der Schlüssel einer Zeile unter „Zu prüfen" — für React und fürs Merken, was neu ist. */
export const pruefschluessel = (z: Pruefzeile): string => `${z.art}-${z.nr}`;

export function Pruefzeilenknopf({ z, neu = false, beiKlick }: {
  z: Pruefzeile;
  /** Kam seit dem letzten Blick dazu (`daten/gesehen.ts`). */
  neu?: boolean;
  /** Wird vor dem Sprung gerufen — die Startseite merkt sich dort „gesehen". */
  beiKlick?: () => void;
}) {
  const t = useT();
  const gehZu = useNavigate();
  const werte = {
    nr: z.nr, kunde: z.kunde, position: z.position ?? "", grund: z.grund ?? "",
    tag: z.tag ? tagKurz(z.tag) : "",
    // Welche Pflichtfotos fehlen (0173) — „vorne, Tank".
    fotos: (z.blickwinkel ?? []).map((w) => t(`pflichtfoto.blickwinkel.${w}`)).join(", "),
    // Was dem Kunden für die Rechnung fehlt (W2) — „Anschrift, Rechnungs-E-Mail".
    luecken: (z.luecken ?? []).map((l) => t(`kunde.luecke.${l}`)).join(", "),
  };
  /* Mit Grund steht der Grund im Satz — „Kunde nicht vor Ort" sagt mehr als
     „nicht bestätigt". Ohne Grund der kürzere Satz. */
  const endung = z.grund ? "Grund" : "";
  /* **Zwei Sätze zu derselben Sache, und beide werden gebraucht.** Kurz in
     der Zeile: was fehlt, in einer Zeile, am Finger lesbar. Lang im `title`:
     derselbe Fall mit Nummer, Kunde und dem, was danach zu tun ist — das
     liest, wer am Schreibtisch mit der Maus darüberfährt. */
  const was = t(`start.zuPruefenWas.${z.art}${endung}`, werte);
  const satz = t(`start.zuPruefenSatz.${z.art}${endung}`, werte);

  /* Wie alt: nur, wo ein Datum da ist. Heute und gestern bekommen ein Wort
     statt einer Zahl — „seit 1 Tagen" schreibt niemand. */
  /* Der Einsatztag, sonst seit wann es liegt (F-02, 24.09.2026): Bis dahin
     trug nur „nicht bestätigt" ein Alter — ein Protokoll ohne Unterschrift
     von eben sah genauso aus wie eines von letzter Woche. */
  const bezug = z.tag ?? z.seit;
  const tage = bezug ? tageSeit(bezug) : null;
  const alter = tage === null || tage < 0
    ? ""
    : tage === 0
      ? t("start.zuPruefenHeute")
      : tage === 1
        ? t("start.zuPruefenGestern")
        : t("start.zuPruefenSeit", { zahl: tage });

  return (
    <div className={`aufgabe pruefen${neu ? " neu" : ""}`}>
      <button
        type="button"
        className="aufgabe-weg"
        title={satz}
        aria-label={`${neu ? `${t("start.neuMarke")}: ` : ""}${t("start.zuPruefenOeffnen", { nr: z.nr, kunde: z.kunde, was })}`}
        /* **Die Werkstattzeile führt in die Ressourcen** (22.09.2026): Bei ihr
           ist `nr` die Kennung eines Geräts und keine Vorgangsnummer — der Weg
           in einen Auftrag liefe ins Leere. */
        onClick={() => {
          beiKlick?.();
          void pruefzeileOeffnen(gehZu, z);
        }}
      >
        <Marke art="acht">{t(`start.zuPruefenArt.${z.art}`)}</Marke>
        <span className="aufgabe-was">
          <b>{z.nr}</b>
          {/* „neu" seit dem letzten Blick (F-02) — ein Zustand, also Marke.
              Neben der Nummer, nicht hinter dem Kunden: Der Name bricht um,
              und dahinter stünde die Marke allein in einer eigenen Zeile. */}
          {neu ? <span className="marke neu">{t("start.neuMarke")}</span> : null}
          <span>{z.kunde}</span>
        </span>
        {/* Leer nur noch bei „kein Fahrer" — siehe den Nachtrag im Kopf
            dieses Blocks. */}
        <span className="aufgabe-zeit">{alter}</span>
        <span className="aufgabe-dazu">{was}</span>
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Eine Zeile in „Heute" — was heute hinausgeht und was zurückkommt.

   Luca, 18.09.2026, am Telefon: *„Was steht heute an? Rein, raus. Die
   Vorgänge sollen eigentlich am besten auch klickbar sein … Ich will dann
   direkt in die Vorgänge kommen."*

   **Die Zeile führt in den Vorgang** (`/auftraege/$nr`) — dort steht, worum
   es geht und was daran zu tun ist. Die Nummer kommt aus `GET /start` und
   reist seit heute mit (`daten/routen.ts`); fehlt sie, bleibt die Zeile
   Text statt eines Knopfs, der ins Leere führt.

   **Die Kante links sagt die Richtung**, bevor irgendjemand die Marke
   liest: Hausgelb hinaus, Grün zurück — dieselben Farben wie der Streifen
   der Tagesmappe (`stile/handy.css`, `.handy-zeile.raus/.rein`).
   ══════════════════════════════════════════════════════════════════ */

export function Heutezeile({ z }: { z: StartKarteZeile }) {
  const t = useT();
  const gehZu = useNavigate();
  const nr = z.vorgang ?? "";

  const inhalt = (
    <>
      {z.marke_key ? <Marke>{t(z.marke_key)}</Marke> : <span />}
      <span className="karte-was">
        <b>{z.kennung}</b>
        <span>{z.wer_was}</span>
      </span>
      {/* „seit 02.10." statt einer Uhrzeit von damals — überfällig oder verspätet (N11-02). */}
      <span className="karte-zeit">{z.zeit_key ? t(z.zeit_key, { datum: z.zeit ?? "" }) : z.zeit}</span>
    </>
  );
  const klasse = `karte-zeile${z.art ? ` ${z.art}` : ""}`;

  if (!nr) return <div className={klasse}>{inhalt}</div>;
  return (
    <button
      className={`${klasse} klickbar`}
      type="button"
      aria-label={t("start.heuteOeffnen", { nr, wer: z.wer_was })}
      onClick={() => void gehZu({ to: "/auftraege/$nr", params: { nr } })}
    >
      {inhalt}
    </button>
  );
}

/**
 * **Eine Wiedervorlage in der Karte „Meine Aufgaben"** (21.09.2026).
 *
 * Luca: *„Bei meinen Aufgaben laufen dann ja auch die Wiedervorlagen rein …
 * Eine Wiedervorlage, die fällig ist, braucht ja auch nicht unter
 * Wiedervorlage zu stehen, wenn sie erst morgen fällig ist. Die reicht ja
 * morgen, und dann reicht sie ja auch unter ‚Meine'."*
 *
 * Dieselben drei Spalten wie die Zeile darüber: das Kennzeichen links, wer
 * und was in der Mitte, die Zeit rechts. „Fällig" trägt die Marke in Acht —
 * heute oder überfällig ist etwas anderes als „nächsten Dienstag".
 */
export function Wiedervorlagezeile({ v }: { v: Wiedervorlage }) {
  const t = useT();
  const gehZu = useNavigate();
  const inhalt = (
    <>
      <Marke {...(v.faellig ? { art: "acht" as const } : {})}>
        {t(v.anlass === "nachfassen" ? "wiedervorlage.markeNachfassen" : "wiedervorlage.markeNotiz")}
      </Marke>
      <span className="karte-was">
        <b>{v.kunde}</b>
        <span>{[v.vorgang, v.ortschaft].filter(Boolean).join(" · ")}</span>
      </span>
      <span className="karte-zeit">{v.wann}</span>
    </>
  );
  if (!v.vorgang) return <div className="karte-zeile">{inhalt}</div>;
  return (
    <button
      className="karte-zeile klickbar"
      type="button"
      aria-label={t("wiedervorlage.oeffnen", { kunde: v.kunde })}
      onClick={() => void gehZu({ to: "/angebote/$nr", params: { nr: v.vorgang } })}
    >
      {inhalt}
    </button>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Eine Fristzeile — Optionsfrist, Qualifikation, Prüftermin.

   Luca, 18.09.2026: *„bei Fristen genauso, weil ich weiß ja gar nicht,
   worum es da geht."* Eine Frist ohne Weg ist eine Mahnung ohne Adresse.

   **Wohin es geht, hängt an der Art** und steht hier, nicht im Server
   (dieselbe Regel wie bei `HAKT` in `daten/routen.ts`): Die Option lebt im
   Brett, wo sie verlängert oder freigegeben wird; Qualifikation und
   Prüftermin hängen an einer Ressource — Maschine und Mensch sind beide
   welche (Regel 8), und beide stehen unter `/ressourcen`.

   **Offen:** Der Vorgang hinter einer Optionsfrist. `GET /start` liefert
   an dieser Stelle Ressource und Kunde, aber nicht `o.number` — bis die
   Route sie mitschickt, endet der Weg im Brett und nicht im Beleg.
   ══════════════════════════════════════════════════════════════════ */

const FRIST_WEG: Record<string, string> = {
  optionsfrist: "/kalender",
  qualifikation: "/ressourcen",
  pruefung: "/ressourcen",
};

/* **Am Telefon gibt es die Planung nicht** (18.09.2026). Luca: *„Das, was
   wirklich bescheuert aussieht, ist die Planung. Die stell hinten an. Das
   soll auf Mobile nicht zur Verfügung stehen."* `/planung` leitet dort auf
   `/start` um (`router.tsx`) — eine Optionsfrist ohne Vorgangsnummer führte
   damit von der Startseite auf die Startseite.

   **Dann bleibt die Zeile lieber Text.** Dieselbe Regel wie bei einer
   Liste, die jemand nicht pflegen darf: Der Weg fehlt, statt zu erscheinen
   und nichts zu tun (28-designkonzept.md, „Die Zeile öffnet den Datensatz").
   Der Regelfall ist davon nicht betroffen: Die Optionsfrist trägt seit dem
   18.09.2026 ihre Vorgangsnummer, und die schlägt den Weg aus der Art. */
/* Seit dem 08.10.2026 heißt der Weg `/kalender` (docs/65); `/planung` bleibt
   darin, solange eine Antwort ihn noch nennt. */
const NUR_AM_SCHREIBTISCH = new Set(["/kalender", "/planung"]);

export function Fristzeile({ z }: { z: StartKarteZeile }) {
  const t = useT();
  const gehZu = useNavigate();
  const telefon = useTelefon();
  const was = z.wer_was || (z.wer_was_key ? t(z.wer_was_key) : "");
  /* Hängt die Frist an einem Vorgang, führt sie dorthin — und nur sonst an
     die Stelle, die zu ihrer Art gehört. Luca, 18.09.2026: *„bei Fristen
     genauso, weil ich weiß ja gar nicht, worum es da geht. Ich will dann
     direkt in die Vorgänge kommen."* Die Nummer liefert `GET /start`; sie
     steht nur an der Optionsfrist, denn Qualifikation und Prüftermin hängen
     an einem Menschen und an einer Maschine, nicht an einem Vorgang. */
  const nr = z.vorgang;
  const ausArt = nr ? undefined : z.art ? FRIST_WEG[z.art] : undefined;
  const weg = ausArt && telefon && NUR_AM_SCHREIBTISCH.has(ausArt) ? undefined : ausArt;
  const oeffnen = nr
    ? () => void gehZu({ to: "/auftraege/$nr", params: { nr } })
    : weg
      ? () => void gehZu({ to: weg })
      : undefined;

  const inhalt = (
    <>
      <Marke {...(z.vorbei ? { art: "stopp" as const } : {})}>{t("aufgaben.art.frist")}</Marke>
      <span className="karte-was">
        <b>{z.kennung}</b>
        <span {...(z.code ? { title: z.code } : {})}>{was}</span>
      </span>
      <span className="karte-zeit">
        {z.zeit_key ? t(z.zeit_key, { zahl: z.zeit ?? "" }) : z.zeit}
      </span>
    </>
  );

  if (!oeffnen) return <div className="karte-zeile">{inhalt}</div>;
  return (
    <button
      className="karte-zeile klickbar"
      type="button"
      aria-label={t("start.fristOeffnen", { was, kennung: z.kennung })}
      onClick={oeffnen}
    >
      {inhalt}
    </button>
  );
}
